import { serializeStyles } from 'rich-text-lib/utils/dom';
import { getEmbedType, sanitizeEmbedHtml as sanitizeInput } from 'rich-text-lib/utils/embedSrc';
import { EmbedFormats } from 'rich-text-lib/constants/embed';
import UILogo from 'UIComponents/image/UILogo';
import { jsx as _jsx } from "react/jsx-runtime";
const parser = new DOMParser();
const GOOGLE_SHEETS_URL = 'docs.google.com/spreadsheets/';
export const IframeContentFormats = {
  URL: 'url',
  CODE: 'code'
};
export const IframeLoadingStatus = {
  NONE: null,
  LOADING: 'loading',
  COMPLETE: 'complete',
  PARSING: 'parsing'
};
export const IFrameOptions = {
  GOOGLE_SHEETS: 'googleSheets',
  YOUTUBE: 'youtube',
  GOOGLE_SLIDES: 'googleSlides',
  GOOGLE_DATA_STUDIO: 'googleDataStudio',
  DATABOX: 'databox',
  AMPLITUDE: 'amplitude',
  OTHER: 'other'
};
export const IFrameContentKeywords = {
  [GOOGLE_SHEETS_URL]: IFrameOptions.GOOGLE_SHEETS,
  'docs.google.com/presentation/': IFrameOptions.GOOGLE_SLIDES,
  'datastudio.google.com/': IFrameOptions.GOOGLE_DATA_STUDIO,
  'app.databox.com/': IFrameOptions.DATABOX,
  'app.amplitude.com/': IFrameOptions.AMPLITUDE,
  'youtube.com/': IFrameOptions.YOUTUBE
};
const ResponsiveIframeStyle = {
  position: 'absolute',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  border: 'none'
};
export const getIframeContentFormat = iframeContent => {
  const embedType = getEmbedType(iframeContent);
  return embedType === EmbedFormats.EMBED ? IframeContentFormats.CODE : embedType;
};
const toArray = nodes => Array.prototype.slice.call(nodes);

// modifies code that user entered so iframe will respond to size changes
const getIframeBodyFromEmbedCode = html => {
  const {
    body
  } = parser.parseFromString(html, 'text/html');

  // concatenating divs to iframe/video selection because we only want to modify
  // those if there isn't an iframe/video tag
  const parentEls = toArray(body.querySelectorAll('iframe,video')).concat(toArray(body.getElementsByTagName('div'))).filter(ele => ele.parentElement === body);
  if (parentEls.length > 0) {
    const node = parentEls[0];
    node.style = serializeStyles(ResponsiveIframeStyle);
    return body.innerHTML;
  }
  return html;
};
const getIframeBodyFromUrl = url => {
  const iframeNode = document.createElement('iframe');
  iframeNode.setAttribute('src', url);
  // @ts-expect-error ts-migrate(2540) FIXME: Cannot assign to 'style' because it is a read-only... Remove this comment to see the full error message
  iframeNode.style = serializeStyles(ResponsiveIframeStyle);
  return iframeNode.outerHTML;
};
export const hasGoogleSheetsRange = c => c.includes('range=') && c.includes(GOOGLE_SHEETS_URL);
export const isGoogleSheetsContentFullDocument = spreadsheetContent => {
  return !spreadsheetContent.includes('single=true');
};
const getGoogleSheetsIframeBodyUrl = (content, startCell, endCell) => {
  return `${content}&range=${startCell}:${endCell}`;
};
const getGoogleSheetsIframeBodyEmbedCode = (content, startCell, endCell) => {
  const regex = /src="[^"]*"/;
  const srcUrl = content.match(regex)[0];
  const newSrcUrl = `${srcUrl.slice(0, -1)}&amp;range=${startCell}:${endCell}"`;
  return content.replace(regex, newSrcUrl);
};
export const validateCellValue = cellValue => {
  const regex = /^[A-z]{1,3}\d+$/;
  return regex.test(cellValue);
};
export const getGoogleSheetsContentWithCellRange = (content, startCell, endCell) => {
  if (isGoogleSheetsContentFullDocument(content) || !validateCellValue(startCell) || !validateCellValue(endCell)) {
    return content;
  }
  const sanitizedValue = sanitizeInput(content);
  const type = getIframeContentFormat(sanitizedValue);
  return type === IframeContentFormats.CODE ? getGoogleSheetsIframeBodyEmbedCode(sanitizedValue, startCell, endCell) : getGoogleSheetsIframeBodyUrl(sanitizedValue, startCell, endCell);
};
export const getIframeBody = iframeContent => {
  const sanitizedValue = sanitizeInput(iframeContent);
  const type = getIframeContentFormat(sanitizedValue);
  if (type !== IframeContentFormats.URL && type !== IframeContentFormats.CODE) {
    return '';
  }
  return type === IframeContentFormats.CODE ? getIframeBodyFromEmbedCode(sanitizedValue) : getIframeBodyFromUrl(sanitizedValue);
};
export const getGoogleSheetsIframeBody = (content, startCell, endCell) => {
  const newContent = getGoogleSheetsContentWithCellRange(content, startCell, endCell);
  return getIframeBody(newContent);
};
export const iframeOptionsText = option => {
  switch (option) {
    case IFrameOptions.GOOGLE_SHEETS:
      return 'iframeWidget.options.googleSheets';
    case IFrameOptions.YOUTUBE:
      return 'iframeWidget.options.youtube';
    case IFrameOptions.GOOGLE_SLIDES:
      return 'iframeWidget.options.googleSlides';
    case IFrameOptions.GOOGLE_DATA_STUDIO:
      return 'iframeWidget.options.googleDataStudio';
    case IFrameOptions.DATABOX:
      return 'iframeWidget.options.databox';
    case IFrameOptions.AMPLITUDE:
      return 'iframeWidget.options.amplitude';
    case IFrameOptions.OTHER:
    default:
      return 'iframeWidget.options.other';
  }
};
export const determineDocumentation = option => {
  switch (option) {
    case IFrameOptions.GOOGLE_SHEETS:
      return 'https://support.google.com/docs/answer/183965?hl=en&ref_topic=2818998#embed_files&zippy=%2Cedit-embedded-spreadsheets';
    case IFrameOptions.YOUTUBE:
      return 'https://support.google.com/youtube/answer/171780?hl=en';
    case IFrameOptions.GOOGLE_SLIDES:
      return 'https://support.google.com/docs/answer/183965?hl=en&ref_topic=2818998#embed_files&zippy=%2Cembed-a-document-spreadsheet-or-presentation';
    case IFrameOptions.GOOGLE_DATA_STUDIO:
      return 'https://support.google.com/datastudio/answer/7450249?hl=en#embed-via-html-iframe&zippy=%2Cin-this-article';
    case IFrameOptions.DATABOX:
      return 'https://databox.com/embed-interactive-dashboards#:~:text=How%20to%20embed%20your%20Databoards';
    case IFrameOptions.AMPLITUDE:
      return 'https://community.amplitude.com/product-updates/embedded-amplitude-338';
    case IFrameOptions.OTHER:
    default:
      return null;
  }
};
export const renderOptionImage = option => {
  const ImageIcon = ({
    logoName
  }) => /*#__PURE__*/_jsx(UILogo, {
    name: logoName,
    width: 72
  });
  switch (option) {
    case IFrameOptions.GOOGLE_SHEETS:
      return /*#__PURE__*/_jsx(ImageIcon, {
        logoName: "google-sheets"
      });
    case IFrameOptions.YOUTUBE:
      return /*#__PURE__*/_jsx(ImageIcon, {
        logoName: "youtube"
      });
    case IFrameOptions.GOOGLE_SLIDES:
      return /*#__PURE__*/_jsx(ImageIcon, {
        logoName: "google-slides"
      });
    case IFrameOptions.GOOGLE_DATA_STUDIO:
      return /*#__PURE__*/_jsx(ImageIcon, {
        logoName: "google-data-studio"
      });
    case IFrameOptions.DATABOX:
      return /*#__PURE__*/_jsx(ImageIcon, {
        logoName: "databox"
      });
    case IFrameOptions.AMPLITUDE:
      return /*#__PURE__*/_jsx(ImageIcon, {
        logoName: "amplitude"
      });
    case IFrameOptions.OTHER:
    default:
      return null;
  }
};
export const parseContentForSelection = option => {
  const currentKeyword = Object.keys(IFrameContentKeywords).find(keyword => option.includes(keyword));
  return currentKeyword ?
  // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  IFrameContentKeywords[currentKeyword] : IFrameOptions.OTHER;
};
const parseReportContentFromEmbedCode = content => {
  const regex = /src="[^"]*"/;
  const srcUrl = content.match(regex)[0];
  const [parsedSrcUrl, ranges] = srcUrl.split('&amp;range=');
  const [parsedStartCell, parsedEndCell] = ranges.split(':');
  const parsedContent = content.replace(regex, `${parsedSrcUrl}"`);
  return parsedContent && parsedStartCell && parsedEndCell ? {
    parsedContent,
    parsedStartCell,
    parsedEndCell: parsedEndCell.slice(0, -1)
  } : {
    parsedContent: content,
    parsedStartCell: '',
    parsedEndCell: ''
  };
};
const parseSheetRanges = (ranges = '') => {
  const [parsedStartCell, parsedEndCell] = ranges.split(':');
  if (parsedEndCell && parsedStartCell) {
    return {
      parsedStartCell,
      parsedEndCell
    };
  }
  return {
    parsedStartCell: '',
    parsedEndCell: ''
  };
};
const parseReportContentFromUrl = (contentUrl = '') => {
  try {
    const cleanedContentUrl = contentUrl.replace(/&amp;/g, '&');
    const urlObj = new URL(cleanedContentUrl);
    const ranges = urlObj.searchParams.get('range') || '';
    urlObj.searchParams.delete('range');
    const parsedContentUrl = urlObj.toString();
    const {
      parsedStartCell,
      parsedEndCell
    } = parseSheetRanges(ranges);
    return {
      parsedContent: parsedContentUrl || contentUrl,
      parsedStartCell,
      parsedEndCell
    };
  } catch (err) {
    return {
      parsedContent: contentUrl,
      parsedStartCell: '',
      parsedEndCell: ''
    };
  }
};
export const parseReportContent = report => {
  if (!report) {
    return {
      parsedContent: '',
      parsedStartCell: '',
      parsedEndCell: ''
    };
  }
  const content = report.getIn(['config', 'content']);
  const sanitizedContent = sanitizeInput(content);
  const type = getIframeContentFormat(sanitizedContent);
  if (type !== IframeContentFormats.URL && type !== IframeContentFormats.CODE || !hasGoogleSheetsRange(content)) {
    return {
      parsedContent: content,
      parsedStartCell: '',
      parsedEndCell: ''
    };
  }
  return type === IframeContentFormats.CODE ? parseReportContentFromEmbedCode(sanitizedContent) : parseReportContentFromUrl(sanitizedContent);
};